import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import {
    ArrowRight, Download, Lock, Eye, CheckCircle,
    FileText, BarChart3, Target, Zap, Search,
    Smartphone, MousePointerClick, TrendingUp,
    AlertTriangle, Calendar, ListChecks, Wrench,
    Mail, User, Loader2
} from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

const REPORT_SECTIONS = [
    {
        icon: FileText,
        title: "Cover Page & Executive Summary",
        description: "Professional branded cover with your business name, website URL, and overall revenue health score.",
        color: "violet",
        locked: false
    },
    {
        icon: BarChart3,
        title: "Revenue Score Breakdown",
        description: "Visual breakdown of your score across all 7 revenue optimization categories with letter grades.",
        color: "cyan",
        locked: false
    },
    {
        icon: AlertTriangle,
        title: "Revenue Leak Analysis",
        description: "Deep-dive into each leak: why it matters, exact revenue impact, and data-backed insights.",
        color: "red",
        locked: true
    },
    {
        icon: Target,
        title: "Priority Fix Order (ROI Ranked)",
        description: "Fixes ranked by ease of implementation and revenue impact so you know exactly where to start.",
        color: "amber",
        locked: true
    },
    {
        icon: Calendar,
        title: "30-Day Action Plan",
        description: "Week-by-week implementation roadmap with specific tasks, tools, and time estimates.",
        color: "green",
        locked: true
    },
    {
        icon: TrendingUp,
        title: "Revenue Projections",
        description: "Conservative, moderate, and aggressive revenue recovery estimates based on your specific leaks.",
        color: "emerald",
        locked: true
    },
    {
        icon: Wrench,
        title: "Tool & Software Recommendations",
        description: "Curated list of tools for each fix — with free alternatives and premium options compared.",
        color: "blue",
        locked: true
    },
    {
        icon: ListChecks,
        title: "DIY vs Done-For-You Comparison",
        description: "Honest breakdown of what you can fix yourself vs. what needs professional help.",
        color: "magenta",
        locked: true
    }
];

const colorMap = {
    violet: { bg: "bg-violet-500/15", border: "border-violet-500/30", text: "text-violet-400", icon: "text-violet-400" },
    cyan: { bg: "bg-cyan-500/15", border: "border-cyan-500/30", text: "text-cyan-400", icon: "text-cyan-400" },
    red: { bg: "bg-red-500/15", border: "border-red-500/30", text: "text-red-400", icon: "text-red-400" },
    amber: { bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-400", icon: "text-amber-400" },
    green: { bg: "bg-green-500/15", border: "border-green-500/30", text: "text-green-400", icon: "text-green-400" },
    emerald: { bg: "bg-emerald-500/15", border: "border-emerald-500/30", text: "text-emerald-400", icon: "text-emerald-400" },
    blue: { bg: "bg-blue-500/15", border: "border-blue-500/30", text: "text-blue-400", icon: "text-blue-400" },
    magenta: { bg: "bg-pink-500/15", border: "border-pink-500/30", text: "text-pink-400", icon: "text-pink-400" }
};

function generateSamplePDF() {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pw = doc.internal.pageSize.getWidth();
    const m = 20;
    const cw = pw - m * 2;

    // === COVER PAGE ===
    doc.setFillColor(10, 10, 10);
    doc.rect(0, 0, pw, 100, 'F');
    doc.setFillColor(139, 92, 246);
    doc.rect(0, 98, pw, 3, 'F');

    doc.setFontSize(14);
    doc.setTextColor(139, 92, 246);
    doc.setFont('helvetica', 'bold');
    doc.text("SITE GRADE PRO", m, 30);

    doc.setFontSize(32);
    doc.setTextColor(255, 255, 255);
    doc.text("Profit Leak", m, 55);
    doc.setTextColor(6, 182, 212);
    doc.text("Fix Plan", m, 70);

    doc.setFontSize(11);
    doc.setTextColor(200, 200, 200);
    doc.setFont('helvetica', 'normal');
    doc.text("SAMPLE REPORT", m, 85);

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(m, 115, cw, 50, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("CUSTOM OPTIMIZATION REPORT FOR:", m + 10, 128);
    doc.setFontSize(18);
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'bold');
    doc.text("Acme Corp (Sample)", m + 10, 142);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(139, 92, 246);
    doc.text("www.example.com", m + 10, 152);

    // Score box
    const sx = pw - m - 50;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(sx - 5, 115, 60, 50, 3, 3, 'F');
    doc.setDrawColor(139, 92, 246);
    doc.setLineWidth(0.5);
    doc.roundedRect(sx - 5, 115, 60, 50, 3, 3, 'S');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text("REVENUE SCORE", sx + 25, 128, { align: 'center' });
    doc.setFontSize(28);
    doc.setTextColor(245, 158, 11);
    doc.setFont('helvetica', 'bold');
    doc.text("30", sx + 25, 148, { align: 'center' });
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text("/ 70", sx + 25, 158, { align: 'center' });

    // Table of contents
    let y = 185;
    doc.setFontSize(14);
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'bold');
    doc.text("What's Inside This Report", m, y);
    y += 12;
    const toc = [
        "1. Executive Summary & Revenue Score",
        "2. Revenue Leak Breakdown (Per Category)",
        "3. Priority Fix Order (ROI Ranked)",
        "4. 30-Day Action Plan",
        "5. Revenue Projections",
        "6. Tool & Software Recommendations",
        "7. DIY vs Done-For-You Comparison"
    ];
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    toc.forEach(item => {
        doc.setTextColor(60, 60, 60);
        doc.text(item, m + 5, y);
        y += 8;
    });

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("Generated by SiteGradePro.com", pw / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });

    // === PAGE 2: EXECUTIVE SUMMARY ===
    doc.addPage();
    doc.setFillColor(10, 10, 10);
    doc.rect(0, 0, pw, 25, 'F');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text("EXECUTIVE SUMMARY", m, 17);

    y = 40;
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'normal');
    const summaryText = "Based on our analysis, Acme Corp scored 30 out of 70 on the Revenue Optimization Scale. This indicates 4 significant revenue leaks that are actively reducing your monthly income. Our analysis estimates you are leaving between $600 - $2,000 in potential monthly revenue on the table.";
    const lines = doc.splitTextToSize(summaryText, cw);
    doc.text(lines, m, y);
    y += lines.length * 6 + 15;

    // Score categories
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text("Score Breakdown", m, y);
    y += 10;

    const cats = [
        { name: "Email Capture System", score: "YES", color: [16, 185, 129] },
        { name: "Abandoned Cart Recovery", score: "NO", color: [239, 68, 68] },
        { name: "Upsells & Product Bundles", score: "NO", color: [239, 68, 68] },
        { name: "Social Proof / Reviews", score: "YES", color: [16, 185, 129] },
        { name: "Page Load Speed", score: "NO", color: [239, 68, 68] },
        { name: "Retargeting Ads", score: "YES", color: [16, 185, 129] },
        { name: "Mobile Optimization", score: "NO", color: [239, 68, 68] }
    ];
    cats.forEach(cat => {
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(m, y - 4, cw, 10, 1, 1, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        doc.text(cat.name, m + 5, y + 2);
        doc.setTextColor(...cat.color);
        doc.setFont('helvetica', 'bold');
        doc.text(cat.score === "YES" ? "PASS" : "LEAK DETECTED", pw - m - 5, y + 2, { align: 'right' });
        y += 13;
    });

    // Blurred / redacted sections
    y += 10;
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(m, y, cw, 60, 3, 3, 'F');
    doc.setDrawColor(139, 92, 246);
    doc.setLineWidth(0.3);
    doc.roundedRect(m, y, cw, 60, 3, 3, 'S');

    doc.setFontSize(12);
    doc.setTextColor(139, 92, 246);
    doc.setFont('helvetica', 'bold');
    doc.text("DETAILED REVENUE LEAK ANALYSIS", m + cw / 2, y + 22, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text("This section contains your personalized leak-by-leak", m + cw / 2, y + 32, { align: 'center' });
    doc.text("breakdown with specific revenue impact estimates.", m + cw / 2, y + 39, { align: 'center' });
    doc.setTextColor(139, 92, 246);
    doc.setFont('helvetica', 'bold');
    doc.text("Available in your full report for $19", m + cw / 2, y + 50, { align: 'center' });

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text("Generated by SiteGradePro.com  |  Sample Report  |  Page 2", pw / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });

    // === PAGE 3: TEASER OF ACTION PLAN ===
    doc.addPage();
    doc.setFillColor(10, 10, 10);
    doc.rect(0, 0, pw, 25, 'F');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text("30-DAY ACTION PLAN (PREVIEW)", m, 17);

    y = 38;
    const weeks = [
        { week: "Week 1", title: "Quick Wins", tasks: ["Set up abandoned cart email sequence", "Enable basic retargeting pixel"], unlocked: true },
        { week: "Week 2", title: "Revenue Recovery", tasks: ["Create product bundles", "Implement upsell flows"], unlocked: false },
        { week: "Week 3", title: "Optimization", tasks: ["Optimize page load speed", "A/B test checkout flow"], unlocked: false },
        { week: "Week 4", title: "Scale & Monitor", tasks: ["Launch retargeting campaigns", "Set up analytics dashboard"], unlocked: false }
    ];
    weeks.forEach(w => {
        doc.setFillColor(w.unlocked ? 248 : 240, w.unlocked ? 250 : 240, w.unlocked ? 252 : 240);
        doc.roundedRect(m, y - 2, cw, 28, 2, 2, 'F');
        doc.setFontSize(11);
        doc.setTextColor(139, 92, 246);
        doc.setFont('helvetica', 'bold');
        doc.text(`${w.week}: ${w.title}`, m + 5, y + 6);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        w.tasks.forEach((task, i) => {
            doc.setTextColor(w.unlocked ? 60 : 180, w.unlocked ? 60 : 180, w.unlocked ? 60 : 180);
            doc.text(w.unlocked ? `  ${task}` : `  [Unlock full report to view]`, m + 8, y + 14 + i * 6);
        });
        y += 32;
    });

    // CTA
    y += 10;
    doc.setFillColor(139, 92, 246);
    doc.roundedRect(m, y, cw, 35, 3, 3, 'F');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text("Get Your Full Personalized Report", pw / 2, y + 15, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text("Run your free audit at SiteGradePro.com and unlock your Fix Plan for just $19", pw / 2, y + 25, { align: 'center' });

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("Generated by SiteGradePro.com  |  Sample Report  |  Page 3", pw / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });

    doc.save("SiteGradePro-Sample-Report.pdf");
}

export default function SampleReportPage({ onRunAudit, onNavigate }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem('sgp_sample_lead');
        if (stored) setHasAccess(true);
    }, []);

    const handleSubmitAndDownload = async (e) => {
        e?.preventDefault();
        if (!name.trim() || !email.trim()) {
            toast.error("Please enter your name and email");
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                name: name.trim(),
                email: email.trim(),
                website: null,
                message: "Sample report PDF download request"
            });
            localStorage.setItem('sgp_sample_lead', JSON.stringify({ name: name.trim(), email: email.trim() }));
            setHasAccess(true);
            toast.success("Downloading your sample report!");
            generateSamplePDF();
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDirectDownload = () => {
        generateSamplePDF();
        toast.success("Sample report downloaded!");
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO
                title="Sample Pro Report | See What You Get | SiteGradePro"
                description="Preview the SiteGradePro Pro report. See the section breakdown, blurred report preview, and download a free sample PDF before you buy."
                keywords="sample website audit report, pro report preview, website fix plan example, SiteGradePro sample"
            />

            <Navbar onNavigate={onNavigate} onRunAudit={onRunAudit} />

            {/* Hero */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-400 text-sm font-medium rounded-full mb-6">
                        <Eye className="h-4 w-4" />
                        Preview Before You Buy
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                        See Exactly What's Inside<br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">Your $19 Pro Report</span>
                    </h1>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
                        10+ pages of personalized, actionable fixes for your website — ranked by ROI with a week-by-week action plan. Here's a preview.
                    </p>
                    {hasAccess ? (
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button
                                onClick={handleDirectDownload}
                                size="lg"
                                className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-8 py-6 text-base"
                                data-testid="download-sample-pdf"
                            >
                                <Download className="mr-2 h-5 w-5" />
                                Download Sample PDF
                            </Button>
                            <Button
                                onClick={onRunAudit}
                                size="lg"
                                variant="outline"
                                className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm px-8 py-6 text-base"
                                data-testid="hero-run-audit"
                            >
                                Get My Report
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmitAndDownload} className="max-w-md mx-auto space-y-3" data-testid="email-capture-form">
                            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                                <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <Input
                                    type="text"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto"
                                    data-testid="capture-name-input"
                                    required
                                />
                            </div>
                            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                                <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <Input
                                    type="email"
                                    placeholder="Your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto"
                                    data-testid="capture-email-input"
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                size="lg"
                                className="w-full bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-8 py-6 text-base"
                                data-testid="capture-submit-btn"
                            >
                                {isSubmitting ? (
                                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...</>
                                ) : (
                                    <><Download className="mr-2 h-5 w-5" /> Get Free Sample PDF</>
                                )}
                            </Button>
                            <p className="text-xs text-gray-600 text-center">No spam. Just your sample report.</p>
                        </form>
                    )}
                </div>
            </section>

            {/* Blurred Report Preview */}
            <section className="py-16 px-4" data-testid="blurred-report-section">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10">
                        Blurred Report Preview
                    </h2>
                    <div className="relative">
                        {/* Simulated PDF Report Pages */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Page 1 - Cover (clear) */}
                            <div className="bg-white rounded-lg shadow-2xl shadow-violet-500/10 overflow-hidden" data-testid="report-page-1">
                                <div className="bg-[#0a0a0a] p-5">
                                    <p className="text-violet-400 text-xs font-bold tracking-wider">SITE GRADE PRO</p>
                                    <p className="text-white text-lg font-bold mt-2">Profit Leak</p>
                                    <p className="text-cyan-400 text-lg font-bold">Fix Plan</p>
                                </div>
                                <div className="p-5 bg-white">
                                    <div className="bg-gray-50 rounded p-3 mb-3">
                                        <p className="text-gray-400 text-[10px] uppercase tracking-wider">Report for</p>
                                        <p className="text-gray-800 font-bold text-sm">Acme Corp</p>
                                        <p className="text-violet-500 text-xs">www.example.com</p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-400 text-[10px]">Revenue Score</span>
                                        <div className="text-center">
                                            <span className="text-amber-500 font-bold text-xl">30</span>
                                            <span className="text-gray-400 text-xs">/70</span>
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-1.5">
                                        <p className="text-gray-600 text-[10px] font-bold uppercase">Contents</p>
                                        {["Executive Summary", "Leak Breakdown", "Priority Fixes", "Action Plan"].map((s, i) => (
                                            <p key={i} className="text-gray-500 text-[10px]">{i + 1}. {s}</p>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Page 2 - Executive Summary (partially blurred) */}
                            <div className="bg-white rounded-lg shadow-2xl shadow-violet-500/10 overflow-hidden relative" data-testid="report-page-2">
                                <div className="bg-[#0a0a0a] px-5 py-3">
                                    <p className="text-white text-xs font-bold">EXECUTIVE SUMMARY</p>
                                </div>
                                <div className="p-5 bg-white">
                                    <p className="text-gray-700 text-[10px] leading-relaxed mb-3">
                                        Based on our analysis, your business scored 30 out of 70 on the Revenue Optimization Scale, indicating 4 significant revenue leaks...
                                    </p>
                                    <p className="text-gray-600 text-[10px] font-bold mb-2">Score Breakdown</p>
                                    <div className="space-y-1.5">
                                        {[
                                            { name: "Email Capture", pass: true },
                                            { name: "Cart Recovery", pass: false },
                                            { name: "Upsells", pass: false },
                                            { name: "Social Proof", pass: true },
                                            { name: "Page Speed", pass: false }
                                        ].map((c, i) => (
                                            <div key={i} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                                                <span className="text-gray-600 text-[10px]">{c.name}</span>
                                                <span className={`text-[10px] font-bold ${c.pass ? 'text-emerald-500' : 'text-red-500'}`}>
                                                    {c.pass ? 'PASS' : 'LEAK'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* Blur overlay on bottom half */}
                                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-white via-white/95 to-transparent backdrop-blur-[2px] flex items-end justify-center pb-4">
                                    <div className="flex items-center gap-1 text-violet-500 text-xs font-medium">
                                        <Lock className="h-3 w-3" />
                                        Full breakdown in Pro report
                                    </div>
                                </div>
                            </div>

                            {/* Page 3 - Action Plan (heavily blurred) */}
                            <div className="bg-white rounded-lg shadow-2xl shadow-violet-500/10 overflow-hidden relative" data-testid="report-page-3">
                                <div className="bg-[#0a0a0a] px-5 py-3">
                                    <p className="text-white text-xs font-bold">30-DAY ACTION PLAN</p>
                                </div>
                                <div className="p-5 bg-white">
                                    <div className="space-y-3">
                                        <div className="bg-gray-50 rounded p-2">
                                            <p className="text-violet-500 text-[10px] font-bold">Week 1: Quick Wins</p>
                                            <p className="text-gray-600 text-[10px]">Set up abandoned cart emails</p>
                                            <p className="text-gray-600 text-[10px]">Enable retargeting pixel</p>
                                        </div>
                                        <div className="bg-gray-50 rounded p-2 blur-[3px]">
                                            <p className="text-violet-500 text-[10px] font-bold">Week 2: Revenue Recovery</p>
                                            <p className="text-gray-500 text-[10px]">Create product bundles...</p>
                                            <p className="text-gray-500 text-[10px]">Implement upsell flows...</p>
                                        </div>
                                        <div className="bg-gray-50 rounded p-2 blur-[3px]">
                                            <p className="text-violet-500 text-[10px] font-bold">Week 3: Optimization</p>
                                            <p className="text-gray-500 text-[10px]">Optimize page load speed...</p>
                                        </div>
                                        <div className="bg-gray-50 rounded p-2 blur-[3px]">
                                            <p className="text-violet-500 text-[10px] font-bold">Week 4: Scale</p>
                                            <p className="text-gray-500 text-[10px]">Launch campaigns...</p>
                                        </div>
                                    </div>
                                </div>
                                {/* Full blur overlay */}
                                <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-white via-white/90 to-transparent flex items-end justify-center pb-4">
                                    <div className="flex items-center gap-1 text-violet-500 text-xs font-medium">
                                        <Lock className="h-3 w-3" />
                                        Unlock for $19
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section Breakdown */}
            <section className="py-16 px-4 bg-gradient-to-b from-transparent to-white/[0.02]" data-testid="section-breakdown">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                            What's Inside Your Report
                        </h2>
                        <p className="text-gray-400">
                            8 sections, 10+ pages of personalized, data-backed recommendations
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {REPORT_SECTIONS.map((section, i) => {
                            const c = colorMap[section.color];
                            const Icon = section.icon;
                            return (
                                <Card key={i} className={`bg-white/[0.03] border-white/10 hover:${c.border} transition-all`} data-testid={`section-card-${i}`}>
                                    <CardContent className="p-5 flex items-start gap-4">
                                        <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0`}>
                                            <Icon className={`h-5 w-5 ${c.icon}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-sm font-semibold text-white">{section.title}</h3>
                                                {section.locked ? (
                                                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400 text-[10px] font-medium">
                                                        <Lock className="h-2.5 w-2.5" />
                                                        Pro
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-medium">
                                                        <Eye className="h-2.5 w-2.5" />
                                                        Preview
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 leading-relaxed">{section.description}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Social Proof / Value Justification */}
            <section className="py-16 px-4">
                <div className="max-w-3xl mx-auto">
                    <Card className="bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border-violet-500/30">
                        <CardContent className="p-8 md:p-10">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                                    $19 Can Save You Thousands
                                </h2>
                                <p className="text-gray-400">
                                    Here's what you're getting compared to hiring a consultant
                                </p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                                <div>
                                    <p className="text-3xl font-bold text-red-400 line-through opacity-60">$500+</p>
                                    <p className="text-xs text-gray-500 mt-1">Typical consultant audit</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-bold text-red-400 line-through opacity-60">$200+</p>
                                    <p className="text-xs text-gray-500 mt-1">Freelancer one-time review</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">$19</p>
                                    <p className="text-xs text-emerald-400 mt-1 font-medium">SiteGradePro Fix Plan</p>
                                </div>
                            </div>
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    "Personalized to YOUR website",
                                    "10+ pages of actionable fixes",
                                    "ROI-ranked priority order",
                                    "30-day step-by-step plan",
                                    "Tool recommendations included",
                                    "Instant download (no waiting)"
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                        <span className="text-sm text-gray-300">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Download Sample + CTA */}
            <section className="py-16 px-4 border-t border-white/5" data-testid="bottom-cta-section">
                <div className="max-w-3xl mx-auto text-center">
                    <FileText className="h-12 w-12 text-violet-400 mx-auto mb-6" />
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                        {hasAccess ? "Ready to Download Again?" : "Still Not Sure? Download the Sample."}
                    </h2>
                    <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                        {hasAccess
                            ? "Your sample report is ready. Download it anytime."
                            : "Get a real 3-page sample PDF with the cover page, executive summary preview, and action plan teaser."
                        }
                    </p>
                    {hasAccess ? (
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button
                                onClick={handleDirectDownload}
                                size="lg"
                                variant="outline"
                                className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm px-8 py-6 text-base"
                                data-testid="download-sample-pdf-bottom"
                            >
                                <Download className="mr-2 h-5 w-5" />
                                Download Sample PDF
                            </Button>
                            <Button
                                onClick={onRunAudit}
                                size="lg"
                                className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-8 py-6 text-base"
                                data-testid="bottom-cta-audit"
                            >
                                Run Free Audit Now
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmitAndDownload} className="max-w-md mx-auto space-y-3" data-testid="email-capture-form-bottom">
                            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                                <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <Input
                                    type="text"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto"
                                    data-testid="capture-name-input-bottom"
                                    required
                                />
                            </div>
                            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                                <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <Input
                                    type="email"
                                    placeholder="Your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto"
                                    data-testid="capture-email-input-bottom"
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                size="lg"
                                className="w-full bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-8 py-6 text-base"
                                data-testid="capture-submit-btn-bottom"
                            >
                                {isSubmitting ? (
                                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...</>
                                ) : (
                                    <><Download className="mr-2 h-5 w-5" /> Get Free Sample PDF</>
                                )}
                            </Button>
                            <p className="text-xs text-gray-600 text-center">No spam. Just your sample report.</p>
                        </form>
                    )}
                </div>
            </section>

            <Footer onNavigate={onNavigate} />
        </div>
    );
}
